import React, { useEffect, useRef, useState } from "react";
import { useParams, NavLink } from "react-router-dom";
import { Images } from "../assets";
import Button from "../components/Button";
import Popup from "../components/Popup";
import { useTranslation } from "react-i18next";
import { Helmet } from "react-helmet";

const PropertiesComponent = ({ title, value }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { t } = useTranslation();

  let liClass;

  switch (title) {
    case t("machineDetails.advantages"):
      liClass = "list-disc ml-5";
      break;
    case t("machineDetails.technicalCharacteristics"):
      liClass = "";
      break;
    case t("machineDetails.options"):
      liClass = "list-disc ml-5";
      break;
    default:
      liClass = "list-disc ml-5";
  }

  return (
    <div className="px-5 py-3 rounded-lg border border-orange text-start">
      {/* Toggle Button */}
      <button
        className="flex justify-between items-center italic text-xl w-full"
        onClick={() => setIsOpen(!isOpen)}
      >
        {title}
        <img
          src={Images.ARROW_DOWN}
          alt=""
          className={`transition-transform duration-300 ${
            isOpen ? "rotate-180" : "rotate-0"
          }`}
        />
      </button>

      {/* Collapsible Content */}
      {isOpen && (
        <ul className="my-5">
          {value?.map((item, index) => (
            <li key={index} className={liClass}>
              {Array.isArray(item) ? (
                <div
                  className={`w-full -ml-3 ${
                    index % 2 !== 0 ? "bg-orange/50" : ""
                  }`}
                >
                  <div className="flex justify-between w-full md:w-2/3 py-1 pr-3">
                    {item.map((subItem, subIndex) => (
                      <span key={subIndex} className="ml-3">
                        {subItem}
                      </span>
                    ))}
                  </div>
                </div>
              ) : (
                item
              )}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

const ImgDiv = ({ selectedImage, machine }) => {
  const divRef = useRef(null);
  const [maxHeight, setMaxHeight] = useState("auto");

  const updateHeight = () => {
    // Give a little bit of time to load the image correctly
    setTimeout(() => {
      if (divRef.current) {
        setMaxHeight(`${divRef.current.offsetHeight}px`);
      }
    }, 100);
  };

  // Update once on mount (in case the image is cached)
  useEffect(() => {
    updateHeight();
  }, []);

  return (
    <div
      ref={divRef}
      className="flex flex-col items-center justify-center w-full xl:w-2/5 min-h-[30rem] shrink-0 gap-5 bg-grey p-10 order-1 xl:order-2"
      style={{ maxHeight }}
    >
      {((selectedImage ?? {}).type ?? "").startsWith("video/") ? (
        <video
          src={selectedImage?.url || ""}
          controls
          className="object-contain w-full sm:w-1/2 xl:w-full h-full"
          onLoadedMetadata={(e) => {
            requestAnimationFrame(updateHeight);
          }}
        />
      ) : (
        <img
          src={selectedImage?.url || ""}
          alt={machine.name}
          onLoad={() => {
            // Use requestAnimationFrame to ensure the layout is updated before measuring
            requestAnimationFrame(updateHeight);
          }}
          className="object-contain w-full sm:w-1/2 xl:w-full h-full"
        />
      )}
    </div>
  );
};

const MachineDetails = () => {
  const { id } = useParams();
  const [machine, setMachine] = useState(null);
  const [selectedImage, setSelectedImage] = useState(null);
  const [machines, setMachines] = useState([]);
  const { t } = useTranslation();

  useEffect(() => {
    const fetchMachine = async () => {
      try {
        const response = await fetch(`/api/machine/${id}`);
        const data = await response.json();
        setMachine(data);
        setSelectedImage(data.pictures ? data.pictures[0] : null);

        // Set default selected product = this machine
        setFormData((prev) => ({
          ...prev,
          product: data.id,
        }));
      } catch (error) {
        console.error("Error fetching machine data:", error);
      }
    };

    const fetchMachines = async () => {
      try {
        const response = await fetch("/api/machine");
        const data = await response.json();
        setMachines(data);
      } catch (error) {
        console.error("Error fetching machines data:", error);
      }
    };

    fetchMachine();
    fetchMachines();
  }, [id]);

  // Popup handling
  const [isPopupOpen, setIsPopupOpen] = useState(false);

  // State for form inputs
  const [formData, setFormData] = useState({
    name: "",
    product: "",
    object: "",
    phonenumber: "",
    message: "",
  });

  // Handle input changes and update state
  const handleChange = (event) => {
    setFormData({
      ...formData,
      [event.target.id]: event.target.value, // Update the field dynamically
    });
  };

  // Handle form submission
  const handleSubmit = (event) => {
    event.preventDefault(); // Prevents page reload
    fetch("/api/contact", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(formData),
    }).then(async (response) => {
      if (response.status !== 204) {
        const data = await response.json();
        console.error(data);
        return;
      }
      setFormData({
        name: "",
        product: "",
        object: "",
        phonenumber: "",
        message: "",
      });
      setIsPopupOpen(false);
    });
  };

  useEffect(() => {
    if (isPopupOpen) {
      document.body.style.overflow = "hidden"; // Disable scrolling
    } else {
      document.body.style.overflow = "auto"; // Enable scrolling
    }

    return () => {
      document.body.style.overflow = "auto"; // Cleanup on unmount
    };
  }, [isPopupOpen]);

  if (!machine || !machine.categories) {
    return <div class="min-h-screen">{t("loading")}</div>;
  }

  return (
    <div className="flex flex-col xl:flex-row gap-5 my-20 mx-5 xl:mx-10">
      <Helmet>
        <meta name="title" content={machine.name} />
        <meta name="description" content={machine.metadescription} />
      </Helmet>

      {/* Back Button */}
      <NavLink to={"/nos-machines"} className={"hidden xl:flex w-fit h-fit"}>
        <div className="w-16 h-16 bg-orange rounded-full flex items-center justify-center mr-5 mb-5 xl:mr-20 hover:brightness-75 transition">
          <img src={Images.ARROW_BACK} alt={t("back")} className="w-8 h-8" />
        </div>
      </NavLink>

      <div className="flex flex-col xl:flex-row w-full gap-5">
        {/* Thumbnail Column -> max-h-[27rem] : shows maximum 3 images at once (3 * 32 + 2 * 5) / 4 = 26.25 */}
        <div className="flex flex-row xl:flex-col gap-5 max-h-[27rem] overflow-scroll scrollbar-hide order-2 xl:order-1">
          {machine.pictures &&
            machine.pictures.map((picture, index) =>
              ((picture ?? {}).type ?? "").startsWith("video/") ? (
                <video
                  key={index}
                  src={(picture ?? {}).url}
                  alt={machine.name}
                  className={`flex-none w-24 h-24 xl:w-32 xl:h-32 object-cover bg-grey cursor-pointer transition-transform duration-200 hover:border-2 hover:border-orange ${
                    selectedImage === picture.url
                      ? "border-2 border-orange"
                      : ""
                  }`}
                  onClick={() => setSelectedImage(picture)}
                />
              ) : (
                <img
                  key={index}
                  src={(picture ?? {}).url}
                  alt={machine.name}
                  className={`flex-none w-24 h-24 xl:w-32 xl:h-32 object-cover bg-grey cursor-pointer transition-transform duration-200 hover:border-2 hover:border-orange ${
                    selectedImage === (picture ?? {}).url
                      ? "border-2 border-orange"
                      : ""
                  }`}
                  onClick={() => setSelectedImage(picture)}
                />
              )
            )}
        </div>

        {/* Main Image Display */}
        <ImgDiv selectedImage={selectedImage} machine={machine} />

        {/* Machine Details */}
        <div className="flex flex-col items-start text-start w-full xl:w-3/5 gap-5 mt-10 md:mt-5 lg:mt-3 xl:mt-0 order-3">
          <h1 className="text-orange text-4xl font-title uppercase -mb-5">
            {machine.name}
          </h1>
          {machine.categories.length < 1 ? (
            ""
          ) : (
            <h2 className="text-xl">
              {machine.categories.map((category) => category.name).join(" - ")}
            </h2>
          )}
          <p>{machine.description}</p>
          <div className="flex flex-col gap-3 w-full">
            <PropertiesComponent
              title={t("machineDetails.advantages")}
              value={machine.advantages.map((adv) => adv.description)}
            />
            <PropertiesComponent
              title={t("machineDetails.technicalCharacteristics")}
              value={machine.technical_characteristics.map((tc) => [
                `${tc.name}`,
                `${tc.value}`,
              ])}
            />
            <PropertiesComponent
              title={t("machineDetails.options")}
              value={machine.options.map((opt) => [opt.name, opt.value])}
            />
          </div>
          <div className="w-full pt-10 mt-auto">
            <Button
              text={t("machineDetails.requestQuote")}
              width="100%"
              py="py-2"
              orientation={"east"}
              onClick={() => setIsPopupOpen(true)}
            />

            {/* Devis Popup */}
            <Popup isOpen={isPopupOpen} onClose={() => setIsPopupOpen(false)}>
              <h2 className="text-xl md:text-2xl lg:text-3xl xl:text-4xl mb-4 font-title uppercase">
                {t("machineDetails.requestQuote")}
              </h2>
              <div className="w-full bg-transparent max-h-[70vh] overflow-y-scroll">
                <form
                  onSubmit={handleSubmit}
                  className="grid grid-cols-2 md:grid-cols-3 gap-x-5 gap-y-5"
                >
                  <div className="flex flex-col items-start col-span-2 md:col-span-1 gap-1">
                    <label htmlFor="name">{t("form.name")}</label>
                    <input
                      id="name"
                      type="text"
                      required
                      className="w-full rounded-lg bg-transparent border border-white p-2"
                      value={formData.name}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col items-start col-span-2 md:col-span-1 gap-1">
                    <label htmlFor="product">{t("form.product")}</label>
                    <div className="relative w-full h-full">
                      <select
                        id="product"
                        className="w-full h-full rounded-lg bg-transparent border border-white p-2 appearance-none pr-10"
                        value={formData.product}
                        onChange={handleChange}
                      >
                        {/* <option value="" disabled>
                          {t("form.chooseProduct")}
                        </option> */}
                        {machines.map((machine) => (
                          <option key={machine.id} value={machine.id}>
                            {machine.name}
                          </option>
                        ))}
                      </select>
                      <div className="pointer-events-none absolute inset-y-0 right-3 flex items-center">
                        <svg
                          width="15"
                          height="9"
                          viewBox="0 0 15 9"
                          fill="none"
                          xmlns="http://www.w3.org/2000/svg"
                        >
                          <line
                            x1="0.353553"
                            y1="0.646447"
                            x2="7.87229"
                            y2="8.16518"
                            stroke="white"
                          />
                          <line
                            x1="7.12789"
                            y1="8.20309"
                            x2="14.6466"
                            y2="0.684353"
                            stroke="white"
                          />
                        </svg>
                      </div>
                    </div>
                  </div>
                  <div className="flex flex-col items-start col-span-2 md:col-span-1 gap-1">
                    <label htmlFor="object">{t("form.object")}</label>
                    <input
                      id="object"
                      type="text"
                      required
                      className="w-full rounded-lg bg-transparent border border-white p-2"
                      value={formData.object}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="flex flex-col items-start col-span-2 md:col-span-1 gap-1">
                    <label htmlFor="phonenumber">{t("form.phoneNumber")}</label>
                    <input
                      id="phonenumber"
                      type="tel"
                      required
                      className="w-full rounded-lg bg-transparent border border-white p-2"
                      value={formData.phonenumber}
                      onChange={handleChange}
                    />
                  </div>
                  <div className="col-span-2 md:col-span-2 flex flex-col items-start gap-1">
                    <label htmlFor="message">{t("form.message")}</label>
                    <textarea
                      id="message"
                      required
                      className="w-full h-32 md:h-[2.6em] rounded-lg bg-transparent border border-white p-2"
                      value={formData.message}
                      onChange={handleChange}
                    />
                  </div>

                  <div className="col-span-2 md:col-span-3 flex justify-end">
                    <Button
                      text={t("form.send")}
                      type="submit"
                      orientation={"north-east"}
                    />
                  </div>
                </form>
              </div>
            </Popup>
          </div>
        </div>
      </div>
    </div>
  );
};

export default MachineDetails;
